import React, { useCallback, useEffect, useState, useRef, useContext } from 'react';
import {
	type LoomVideo,
	type SDKState,
	type SDKUnsupportedReasons,
	type SDKButtonInterface,
} from '@loomhq/record-sdk';
import { defineMessages, FormattedMessage } from 'react-intl';

import Link from '@atlaskit/link/link';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { expVal } from '@confluence/feature-experiments';
import { getTaskManager, LoadingPriority } from '@confluence/loadable';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { RECORD_LOOM_ENTRY_POINT } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import type { NonMasteredUserModalProps } from './nonMasteredUserModal/NonMasteredUserModal.tsx';
import { NonMasteredUserModalLoader } from './nonMasteredUserModal/NonMasteredUserModalLoader.tsx';
import {
	INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC,
	OPEN_LOOM_RECORDER_SDK_INTERACTION_METRIC,
} from './perf.config.ts';
import { useLoomEntryPointGlobalState } from './useLoomEntryPointGlobalState.ts';
import type { StateType } from './useLoomEntryPointGlobalState.ts';
import { useLoomEntryPointVariant } from './useLoomEntryPointVariant.ts';

export const i18n: {
	errorTitleCannotStart: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorTitleRecordingAlreadyInProgress: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorTitleNotReady: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorIncompatibleBrowser: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorThirdPartyCookieDisabled: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorNoMediaStreamSupport: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorSDKNotInitialized: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorBackendFFNotEnabledYet: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorUnexpectedAuthTokenFailure: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorLoomUserNotCreatedYet: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorSDKFailedToInitialize: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorRecordingAlreadyInProgress: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	errorTitleCannotStart: {
		id: 'loom-utils.error-flag.title.cannot-start',
		defaultMessage: 'We couldn’t start your recording',
		description:
			'title for a notification flag when a user encounters an error that prevents SDK from starting',
	},
	errorTitleRecordingAlreadyInProgress: {
		id: 'loom-utils.error-flag.title.recording-already-in-progress',
		defaultMessage: 'Recording already in progress',
		description:
			'title for a notification flag when a user encounters an error because a recording is already in progress',
	},
	errorTitleNotReady: {
		id: 'loom-utils.error-flag.title.not-ready',
		defaultMessage: 'The Loom recorder wasn’t ready',
		description: 'title for a notification flag when SDK is not ready to record',
	},
	errorIncompatibleBrowser: {
		id: 'loom-utils.error-flag.description.incompatible-browser',
		defaultMessage:
			'The browser you’re currently using isn’t compatible with Loom (or you could be trying to use it in private/incognito mode). <a>Which browsers are compatible?</a>',
		description:
			'description for an error flag when a user encounters a Loom SDK incompatible browser error',
	},
	errorThirdPartyCookieDisabled: {
		id: 'loom-utils.error-flag.description.third-party-cookie-disabled',
		defaultMessage: 'Check that you have third-party cookies enabled, then try again.',
		description:
			'description for an error flag when a user encounters a Loom SDK third-party cookie disabled error',
	},
	errorNoMediaStreamSupport: {
		id: 'loom-utils.error-flag.description.no-media-stream-support',
		defaultMessage:
			'You might need to try a different browser. <a>Which browsers are compatible?</a>',
		description:
			'description for an error flag when a user encounters a Loom SDK no media stream support error',
	},
	errorSDKNotInitialized: {
		id: 'loom-utils.error-flag.description.sdk-not-initialized',
		defaultMessage: 'Try to start recording again now.',
		description: 'description for an error flag when Loom SDK is not yet initialized',
	},
	errorBackendFFNotEnabledYet: {
		id: 'loom-utils.error-flag.description.be-ff-not-enabled-yet',
		defaultMessage: 'Loom was recently enabled. Try again in a few minutes.',
		description:
			'When the integration between Loom and Confluence is first enabled, the server and frontend code arent initially in sync. If the frontend is enabled before the server and a user tries to record a Loom, we will show an error flag with this description, and the issue should resolve on its own within a few minutes',
	},
	errorUnexpectedAuthTokenFailure: {
		id: 'loom-utils.error-flag.description.unexpected-auth-token-failure',
		defaultMessage:
			'Something went wrong with the Loom recorder. Try again later, or contact Atlassian Support for help.',
		description:
			'description for an error flag when the user tries to record a Loom but the auth token fails unexpectedly. We dont have a specific error message to show the user, so we show this generic message instead.',
	},
	errorLoomUserNotCreatedYet: {
		id: 'loom-utils.error-flag.description.loom-user-not-created-yet',
		defaultMessage:
			'Loom is enabled, just give us a few minutes to set everything up. You can try recording again in a bit.',
		description:
			'description for an error flag when the user tries to record a Loom but the user hasnt been created on Looms side yet. It can take a couple of minutes to create, so it will probably work if they try again later.',
	},
	errorSDKFailedToInitialize: {
		id: 'loom-utils.error-flag.description.sdk-failed-to-initialize',
		defaultMessage: 'Give it a moment and then refresh to try again.',
		description: 'description for an error flag when Loom SDK fails to initialize',
	},
	errorRecordingAlreadyInProgress: {
		id: 'loom-utils.error-flag.description.recording-already-in-progress',
		defaultMessage: 'Finish your current Loom video so you can start another.',
		description: 'description for an error flag when a recording is already in progress',
	},
});

export type OpenLoomRecorderParams = {
	onInsert: (loomVideoUrl: string, video: LoomVideo) => void;
	insertButtonText: string;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- analytics-source field, not the browser `location` global
	location?: string;
	/**
	 * Fired each time the recorder UI becomes visible — including the SDK's
	 * "Record again" re-entry. Dedupe consumer-side for once-per-session signals.
	 */
	onRecorderVisible?: () => void;
	/** Fired when the recorder UI has fully closed (insert/cancel/complete). */
	onRecorderHidden?: () => void;
};

type useLoomRecorderEntryPointResponse = {
	isLoomRecorderInitialized: boolean;
	hasRecordingAccess: boolean;
	isOpeningRecorder: boolean;
	openLoomRecorder: (params?: OpenLoomRecorderParams) => Promise<void>;
};

export type UseLoomRecorderEntryPointProps = {
	entryPointLocation: string;
	resetInsertFunctionOnUnmount?: boolean;
	flags: FlagsStateContainer;
};

type UseLoomRecorderEntryPointType = (
	props: UseLoomRecorderEntryPointProps,
) => useLoomRecorderEntryPointResponse;

const LoomSDKSupportLink = 'https://support.loom.com/hc/en-us/articles/360020667617-loomSDK-FAQ';

function getUnsupportedSDKErrorDescription(error: SDKUnsupportedReasons | undefined) {
	if (error === 'third-party-cookies-disabled') {
		return <FormattedMessage {...i18n.errorThirdPartyCookieDisabled} />;
	} else if (error === 'no-media-streams-support') {
		return (
			<FormattedMessage
				{...i18n.errorNoMediaStreamSupport}
				values={{
					a: (chunks: React.ReactNode[]) => (
						<Link href={LoomSDKSupportLink} target="_blank">
							{chunks}
						</Link>
					),
				}}
			/>
		);
	} else if (error === 'incompatible-browser') {
		return (
			<FormattedMessage
				{...i18n.errorIncompatibleBrowser}
				values={{
					a: (chunks: React.ReactNode[]) => (
						<Link href={LoomSDKSupportLink} target="_blank">
							{chunks}
						</Link>
					),
				}}
			/>
		);
	}
}

export const useLoomRecorderEntryPoint: UseLoomRecorderEntryPointType = ({
	entryPointLocation,
	flags,
	resetInsertFunctionOnUnmount = false,
}) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { cloudId, environment } = useSessionData();
	const { entryPointVariant } = useLoomEntryPointVariant();

	const [singletonLoomSDK, { initLoomSDK }] = useLoomEntryPointGlobalState();
	const [isOpeningRecorder, setIsOpeningRecorder] = useState(false);
	const runningSDKButton = useRef<SDKButtonInterface | null>(null);

	// the call site for openLoomRecorder might be outside of React lifecycle
	// (e.g. editor DOM button onclick) so we need to ensure we maintain reference
	const singletonLoomSDKRef = useRef<StateType>(singletonLoomSDK);
	singletonLoomSDKRef.current = singletonLoomSDK;

	const { showModal } = useDialogs();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (entryPointVariant === 'BETA' || entryPointVariant === 'CO_USE') {
			INITIALIZE_LOOM_RECORDER_SDK_INTERACTION_METRIC.start();

			switch (expVal('cc-loom-recorder-init-as-background-loadable', 'cohort', 'control')) {
				case 'background':
					void getTaskManager().push({
						id: 'LoomRecorderSDKBackgroundInit',
						priority: LoadingPriority.BACKGROUND,
						task: async () => void initLoomSDK({ cloudId, environment, entryPointLocation }),
					});
					break;
				case 'lazy':
					void getTaskManager().push({
						id: 'LoomRecorderSDKLazyInit',
						priority: LoadingPriority.LAZY,
						task: async () => void initLoomSDK({ cloudId, environment, entryPointLocation }),
					});
					break;
				default:
					void initLoomSDK({ cloudId, environment, entryPointLocation });
			}
		}
	}, [initLoomSDK, cloudId, environment, entryPointVariant, entryPointLocation]);

	useEffect(() => {
		return () => {
			if (
				resetInsertFunctionOnUnmount &&
				runningSDKButton.current &&
				singletonLoomSDKRef.current.initializedLoomSDK
			) {
				singletonLoomSDKRef.current.initializedLoomSDK.updateConfig({
					config: {
						insertButtonText: 'Open on Loom',
					},
				});

				runningSDKButton.current.off('insert-click');
				runningSDKButton.current.on('insert-click', (video: LoomVideo) => {
					window.open(video.sharedUrl, '_blank');
					experienceTracker.succeed({
						name: RECORD_LOOM_ENTRY_POINT,
						attributes: {
							inserted: true,
							insertFunctionWasReset: true,
						},
					});

					/**
					 * Why do we need to add sendUIEvent if we are already sending a experienceTracker.succeed(ui taskSuccess) event?
					 * "ui taskSuccess" does not have Statsig as destination, so we need to send a sendUIEvent to Statsig
					 */
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							source: entryPointLocation,
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'insertVideoLink',
							attributes: {
								insertFunctionWasReset: true,
							},
						},
					}).fire();
				});
			}
		};
	}, [entryPointLocation, resetInsertFunctionOnUnmount, experienceTracker, createAnalyticsEvent]);

	const openLoomRecorder = useCallback(
		async (loomRecorderParams?: OpenLoomRecorderParams) => {
			if (
				singletonLoomSDKRef.current.initializedLoomSDK &&
				singletonLoomSDKRef.current.initializedLoomSDK.status().state === 'active-recording'
			) {
				void flags.showInfoFlag({
					title: <FormattedMessage {...i18n.errorTitleRecordingAlreadyInProgress} />,
					description: <FormattedMessage {...i18n.errorRecordingAlreadyInProgress} />,
				});

				return;
			}

			experienceTracker.start({
				name: RECORD_LOOM_ENTRY_POINT,
				attributes: {
					entryPointLocation,
				},
			});
			if (singletonLoomSDKRef.current.isInitializing) {
				void flags.showInfoFlag({
					title: <FormattedMessage {...i18n.errorTitleNotReady} />,
					description: <FormattedMessage {...i18n.errorSDKNotInitialized} />,
				});

				experienceTracker.abort({
					name: RECORD_LOOM_ENTRY_POINT,
					reason: 'SDK has not been configured yet.',
				});
				return;
			}
			// TODO CTIA- 2019: fire a clicked UI event here
			if (singletonLoomSDKRef.current.isLoomSDKSupportedError) {
				const description = getUnsupportedSDKErrorDescription(
					singletonLoomSDKRef.current.isLoomSDKSupportedError,
				);

				void flags.showInfoFlag({
					title: <FormattedMessage {...i18n.errorTitleCannotStart} />,
					description,
				});

				experienceTracker.abort({
					name: RECORD_LOOM_ENTRY_POINT,
					reason: `SDK not supported: ${singletonLoomSDKRef.current.isLoomSDKSupportedError}.`,
				});
				return;
			}
			if (!singletonLoomSDKRef.current.hasRecordingAccess) {
				const noRecordingAccessReason = singletonLoomSDKRef.current.noRecordingAccessReason;
				if (noRecordingAccessReason === 'backendFF') {
					void flags.showInfoFlag({
						title: <FormattedMessage {...i18n.errorTitleCannotStart} />,
						description: <FormattedMessage {...i18n.errorBackendFFNotEnabledYet} />,
					});

					experienceTracker.abort({
						name: RECORD_LOOM_ENTRY_POINT,
						reason: 'loom enablement in progress',
					});
				} else if (noRecordingAccessReason === 'not-created') {
					void flags.showInfoFlag({
						title: <FormattedMessage {...i18n.errorTitleCannotStart} />,
						description: <FormattedMessage {...i18n.errorLoomUserNotCreatedYet} />,
					});

					experienceTracker.abort({
						name: RECORD_LOOM_ENTRY_POINT,
						reason: 'user not created yet',
					});
				} else if (
					noRecordingAccessReason === 'not-mastered' ||
					noRecordingAccessReason === 'not-mastered-enterprise'
				) {
					// mastering for enterprise users is not available until M3
					const isMasteringFlowEnabledForEdition =
						noRecordingAccessReason !== 'not-mastered-enterprise';

					showModal<NonMasteredUserModalProps>(NonMasteredUserModalLoader, {
						isMasteringFlowEnabled: isMasteringFlowEnabledForEdition,
					});
					experienceTracker.abort({
						name: RECORD_LOOM_ENTRY_POINT,
						reason: 'Does not have access to record looms from Confluence.',
						attributes: {
							noRecordingAccessReason,
						},
					});
				} else {
					void flags.showInfoFlag({
						title: <FormattedMessage {...i18n.errorTitleCannotStart} />,
						description: <FormattedMessage {...i18n.errorUnexpectedAuthTokenFailure} />,
					});

					experienceTracker.fail({
						name: RECORD_LOOM_ENTRY_POINT,
						error:
							singletonLoomSDKRef.current.authTokenError ||
							new Error('unexpected error from Loom auth token or Loom user status'),
					});
				}
				return;
			}
			if (
				!singletonLoomSDKRef.current.initializedLoomSDK ||
				!singletonLoomSDKRef.current.initializedLoomSDK.status().success
			) {
				void flags.showInfoFlag({
					title: <FormattedMessage {...i18n.errorTitleCannotStart} />,
					description: <FormattedMessage {...i18n.errorSDKFailedToInitialize} />,
				});

				experienceTracker.fail({
					name: RECORD_LOOM_ENTRY_POINT,
					error:
						singletonLoomSDKRef.current.initializedLoomSDKError ||
						new Error('Loom SDK did not successfully initialize.'),
					attributes: {
						loomSDKSuccessStatus: singletonLoomSDKRef.current.initializedLoomSDK?.status()?.success,
					},
				});

				return;
			}
			OPEN_LOOM_RECORDER_SDK_INTERACTION_METRIC.start();
			setIsOpeningRecorder(true);
			createAnalyticsEvent({
				// a track event is fired once recorder is opened, this event is speficially used for monitoring in StatSig
				type: 'sendTrackEvent',
				data: {
					source: entryPointLocation,
					action: 'opened',
					actionSubject: 'loomSdkRecorder',
				},
			}).fire();
			const { configureButton, updateConfig } = singletonLoomSDKRef.current.initializedLoomSDK;

			const sdkButton = configureButton();
			updateConfig({
				config: {
					insertButtonText: loomRecorderParams ? loomRecorderParams.insertButtonText : undefined,
					entryPointName: entryPointLocation,
				},
			});

			if (loomRecorderParams) {
				sdkButton.on('insert-click', async (video: LoomVideo) => {
					// TODO CTIA-2019: fire a inserted track event here
					loomRecorderParams.onInsert(video.sharedUrl, video);

					experienceTracker.succeed({
						name: RECORD_LOOM_ENTRY_POINT,
						attributes: {
							inserted: true,
						},
					});
					/**
					 * Why do we need to add sendUIEvent if we are already sending a experienceTracker.succeed(ui taskSuccess) event?
					 * "ui taskSuccess" does not have Statsig as destination, so we need to send a sendUIEvent to Statsig
					 */
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							source: entryPointLocation,
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'insertVideoLink',
							attributes: {
								insertFunctionWasReset: false,
							},
						},
					}).fire();
				});
			}

			sdkButton.on('start', () => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: entryPointLocation,
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'loomRecordingStart',
					},
				}).fire();
			});

			sdkButton.on('recording-start', () => {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: entryPointLocation,
						action: 'started',
						actionSubject: 'loomRecording',
					},
				}).fire();
			});

			sdkButton.on('cancel', () => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: entryPointLocation,
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'loomRecordingCancel',
					},
				}).fire();
			});

			sdkButton.on('complete', () => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: entryPointLocation,
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'loomRecordingComplete',
						attributes: {
							location: loomRecorderParams?.location,
						},
					},
				}).fire();
			});

			sdkButton.on('recording-complete', () => {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: entryPointLocation,
						action: 'completed',
						actionSubject: 'loomRecording',
						attributes: {
							location: loomRecorderParams?.location,
						},
					},
				}).fire();
			});

			sdkButton.on('upload-complete', () => {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: entryPointLocation,
						action: 'uploaded',
						actionSubject: 'loomRecording',
					},
				}).fire();
			});

			sdkButton.on('lifecycle-update', (lifecycleState: SDKState) => {
				if (lifecycleState === 'pre-recording') {
					OPEN_LOOM_RECORDER_SDK_INTERACTION_METRIC.stop();
					setIsOpeningRecorder(false);
					// `lifecycle-update` re-enters 'pre-recording' on the SDK's
					// "Record again", so this fires on every re-record — consumers
					// tracking recorder visibility (e.g. to keep a host modal
					// hidden across re-records) need each transition. Callers that
					// want a once-per-session signal (e.g. analytics) dedupe their
					// side. Failures surface via the entry-point experience since
					// this callback is part of its contract.
					try {
						loomRecorderParams?.onRecorderVisible?.();
					} catch (error) {
						experienceTracker.fail({
							name: RECORD_LOOM_ENTRY_POINT,
							error: error instanceof Error ? error : new Error(String(error)),
						});
					}
				} else if (lifecycleState === 'closed') {
					runningSDKButton.current = null;
					// Run the consumer callback first so a throw fails the
					// entry-point experience (instead of silently succeeding
					// then swallowing the error).
					try {
						loomRecorderParams?.onRecorderHidden?.();
						experienceTracker.succeed({
							name: RECORD_LOOM_ENTRY_POINT,
							attributes: {
								closed: true,
								insertFunctionDefined: loomRecorderParams !== undefined,
							},
						});
					} catch (error) {
						experienceTracker.fail({
							name: RECORD_LOOM_ENTRY_POINT,
							error: error instanceof Error ? error : new Error(String(error)),
						});
					}
				} else if (lifecycleState === 'cascaded') {
					experienceTracker.succeed({
						name: RECORD_LOOM_ENTRY_POINT,
						attributes: {
							cascaded: true,
							entryPointLocation,
						},
					});
				}
			});

			sdkButton.openPreRecordPanel();
			runningSDKButton.current = sdkButton;
		},
		[entryPointLocation, experienceTracker, flags, showModal, createAnalyticsEvent],
	);

	return {
		openLoomRecorder,
		isOpeningRecorder,
		isLoomRecorderInitialized: !singletonLoomSDKRef.current.isInitializing,
		hasRecordingAccess: singletonLoomSDKRef.current.hasRecordingAccess,
	};
};
